import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import {
  Calendar, Database, FilePlus2, Mail, MessageSquare, Search, Trash2, Wrench, type LucideIcon,
} from 'lucide-react';
import type { AgentOrbState } from './agent-orb';

/** Lets a page (e.g. Probar) tell the surrounding frame's orb what the agent is doing. */
interface Activity { state: AgentOrbState | null; toolIcon: LucideIcon; set: (state: AgentOrbState | null, toolIcon?: LucideIcon) => void }

const noop: Activity = { state: null, toolIcon: Wrench, set: () => {} };
const AgentOrbActivityContext = createContext<Activity>(noop);

export function AgentOrbActivityProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<{ state: AgentOrbState | null; toolIcon: LucideIcon }>({ state: null, toolIcon: Wrench });
  const value = useMemo<Activity>(() => ({
    ...current,
    set: (state, toolIcon = Wrench) => setCurrent({ state, toolIcon }),
  }), [current]);
  return <AgentOrbActivityContext.Provider value={value}>{children}</AgentOrbActivityContext.Provider>;
}

export function useAgentOrbActivity() {
  return useContext(AgentOrbActivityContext);
}

/** Pick an icon for a tool call by its name (query_records → search, send_whatsapp → message…). */
export function toolIconFor(name: string): LucideIcon {
  const n = name.toLowerCase();
  if (/whatsapp|slack|telegram|message|send|notify|reply/.test(n)) return MessageSquare;
  if (/mail|email|gmail/.test(n)) return Mail;
  if (/calendar|schedule|ritual|event/.test(n)) return Calendar;
  if (/delete|remove/.test(n)) return Trash2;
  if (/create|insert|add|update|write/.test(n)) return FilePlus2;
  if (/search|query|find|list|get|read|knowledge/.test(n)) return Search;
  if (/record|entity|table|sql/.test(n)) return Database;
  return Wrench;
}
